import { CreateFormResponseInput, FormField, FormResponseOwnerTypeEnum } from '@alice-financial/alice-sdk'
import { Button, CircularProgress, FormHelperText, Grid } from '@alice-financial/pretext-ui'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import UploadIcon from '@mui/icons-material/Upload'
import * as React from 'react'
import { useCurrentOrgFormFields } from '../../orgDashboard/survey/useOrgFormFields'
import { useOrgId } from '../../orgDashboard/useOrgId'
import { FormFieldUploadOwnerType, useFormFieldUpload } from '../api/useFormFieldUpload'
import { QuestionContainer, QuestionSubtext, QuestionText } from '../surveyBlocks'
import { FileUploadQuestion } from '../types'
import { fieldAnswer } from './formFieldUtils'

const FILE_CONTENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/rtf',
]

type FileUploadInputDisplayProps = Pick<
  CreateFormResponseInput,
  'ownerId' | 'ownerType' | 'formName' | 'formVersion'
> & {
  answer: FormField['answer']
  questionId: string
  questionText: string
  questionSubtext?: string
  required: boolean
  onUpload?: () => void
}
type OrgFileUploadInputDisplayConfigProps = {
  questionText: string
  questionSubtext?: string
  questionId: string
  required: boolean
}

export const makeOrgFileUploadInputDisplayConfig = ({
  questionText,
  questionSubtext,
  questionId,
  required,
}: OrgFileUploadInputDisplayConfigProps) => {
  const question = {
    questionText,
    questionSubtext,
    questionId,
    required,
    fileUpload: true,
    getFieldValue: (answer) => answer || '',
    getAnswer: (values) => values[questionId] || '',
  } as const satisfies FileUploadQuestion
  return {
    ...question,
    Input: ({
      formName,
      formVersion,
      onUpload,
    }: Pick<FileUploadInputDisplayProps, 'formName' | 'formVersion' | 'onUpload'>) => {
      const ownerId = useOrgId()
      const ownerType = FormResponseOwnerTypeEnum.Org
      const formFields = useCurrentOrgFormFields(formName, formVersion)
      const initialAnswer = fieldAnswer(formFields, questionId) || ''
      return (
        <FileUploadInputDisplay
          questionText={questionText}
          questionSubtext={questionSubtext}
          questionId={questionId}
          required={required}
          ownerId={ownerId}
          ownerType={ownerType}
          formName={formName}
          formVersion={formVersion}
          answer={initialAnswer}
          onUpload={onUpload}
        />
      )
    },
  }
}

const getOwnerTypeValue = (ownerType: FormResponseOwnerTypeEnum): FormFieldUploadOwnerType => {
  switch (ownerType) {
    case FormResponseOwnerTypeEnum.Org:
      return 'Employer::Organization'
  }
}

export const FileUploadInputDisplay = ({
  answer,
  ownerId,
  ownerType,
  questionText,
  questionSubtext,
  questionId,
  formName,
  formVersion,
  required,
  onUpload,
}: FileUploadInputDisplayProps) => {
  const [newFileUrl, setNewFileUrl] = React.useState<string | null>(null)
  const fileUrl = newFileUrl || answer
  const { mutate: uploadFile, isLoading: isUploading } = useFormFieldUpload({
    onSuccess: (response) => {
      setNewFileUrl(response?.file.url)
      onUpload?.()
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.item(0)
    if (file) {
      uploadFile({
        file,
        owner_id: ownerId,
        owner_type: getOwnerTypeValue(ownerType),
        question_id: questionId,
        form_name: formName,
        form_version: formVersion,
        question: questionText,
      })
    }
  }
  const inputId = `upload-file-${questionId}`
  return (
    <QuestionContainer>
      <QuestionText required={required}>{questionText}</QuestionText>
      {questionSubtext && <QuestionSubtext gutterBottom>{questionSubtext}</QuestionSubtext>}
      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <input
            onChange={handleFileChange}
            accept={FILE_CONTENT_TYPES.join(',')}
            style={{ display: 'none' }}
            id={inputId}
            type="file"
            required={required}
          />
          <Button
            variant="contained"
            color="primary"
            component="label"
            htmlFor={inputId}
            sx={{ textWrap: 'nowrap' }}
            startIcon={fileUrl ? <SwapHorizIcon /> : <UploadIcon />}
            fullWidth
          >
            {fileUrl ? 'Replace file' : 'Upload file'}
          </Button>
          <FormHelperText>PDF, Word, Text, RTF, Excel, CSV</FormHelperText>
        </Grid>
        {fileUrl && (
          <Grid size={4}>
            {isUploading ? (
              <CircularProgress />
            ) : (
              <Button component="a" href={fileUrl}>
                Download current file
              </Button>
            )}
          </Grid>
        )}
      </Grid>
    </QuestionContainer>
  )
}
